<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="showLoader || (!showLoader && !applyErrorState && !getPortfolioList.length)"
    :showLoader="showLoader"
    :applyErrorState="applyErrorState"
    :fullWhiteBox="getMobileAppState"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <transition-group name="common-enter-animation" mode="out-in" appear>
            <card-product-selector-wrapper
            v-if="!showLoader && !applyErrorState && getPortfolioList.length"
            invertTitleAndDescription
            variant="with-box"
            :productsList="parsedProductList"
            @productClicked="[productSelected = $event, showDetailModal = true]"
            @rightTopButtonClicked="[productSelected = $event, showDetailModal = true]"
            @moreOptionsClicked="moreOptionsClickedHandler"/>
        </transition-group>
        <!-- Empty State -->
        <template #empty-state-slot>
            <empty-state-generic
            v-if="!showLoader && !applyErrorState && !getPortfolioList.length"
            hasButton
            illustration="emptyState/savings"
            :buttonLabel="t('savings.mySavings.portfolio.emptyStateButton')"
            :title="t('savings.mySavings.portfolio.emptyStateTitle')"
            :description="t('savings.mySavings.portfolio.emptyStateDescription')"
            @clicked="navigateByName(routeList.SAVINGS_PRODUCTS_NAME)"/>
        </template>
    </wrapper-features-templates-one-column>
    <!-- Error Feedback -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
    <!-- Details Modal -->
    <details-modal
    v-if="showDetailModal"
    :productSelected="productSelected"
    @chargeProduct="navigateToMySavingsCharge(productSelected)"
    @mobilizeProduct="navigateToMySavingsMobilize(productSelected)"
    @closeMe="showDetailModal = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import CardProductSelectorWrapper from '@/components/generic/cards/productSelector/SelectorsWrapper'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import DetailsModal from '@/components/features/savings/mySavings/portfolio/DetailModal'

/* Vue */
import { ref, computed, onBeforeUnmount } from 'vue'
/* Assets and Helpers */
import { amountFormat } from '@/assets/scripts/amounts'
import { accountFormatHelper } from '@/assets/scripts/strings'
import { getDate } from '@/assets/scripts/dateBuilders'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

const {	portfolioFetch, clearFeatureStates } = mapActions(moduleMapper.SAVINGS_MY_SAVINGS_PORTFOLIO)
const { getPortfolioList } = mapGetters(moduleMapper.SAVINGS_MY_SAVINGS_PORTFOLIO)
const { getMobileAppState } = mapGetters()

/* Data */
const showLoader = ref(false)
const applyErrorState = ref(false)
const showDetailModal = ref(false)
const productSelected = ref()

const parsedProductList = computed(() => {
    if (!getPortfolioList.value.length) return []

    return getPortfolioList.value.map(product => {
        return {
            ...product,
            hasClickOnProduct: true,
            title: product.termAccountProduct.name || '-',
            description: accountFormatHelper(product.accountNumber),
            descriptionClass: 'tiny',
            textBlocks: [{
                label: t('savings.mySavings.portfolio.balanceLabel'),
                content: amountFormat(product.balance, product.currency)
            }, {
                label: t('savings.mySavings.portfolio.interestRateLabel'),
                content: product.interestRate ? `${product.interestRate}%` : '-'
            }, {
                label: t('savings.mySavings.portfolio.durationLabel'),
                content: t('savings.mySavings.portfolio.durationContent', { numberOfDays: product.numberOfDays, ageInDays: product.ageInDays })
            }, {
                label: t('savings.mySavings.portfolio.maturityDateLabel'),
                content: getDate(product.maturityDate),
                classes: 'capitalize'
            }],
            moreOptions: !product.termAccountProduct.canBeIncreased && !product.termAccountProduct.canBeMobilized ? null : createProductMoreOptions(product)
        }
    })
})

const createProductMoreOptions = product => {
    const moreOptions = []

    if (product.termAccountProduct.canBeIncreased) {
        moreOptions.push({
            id: 0, // using indexes as ids to help get callbackfunction
            text: t('savings.mySavings.portfolio.charge'),
            callbackFunction: product => { navigateToMySavingsCharge(product) }
        })
    }

    if (product.termAccountProduct.canBeMobilized) {
        moreOptions.push({
            id: 1, // using indexes as ids to help get callbackfunction
            text: t('savings.mySavings.portfolio.mobilize'),
            callbackFunction: product => { navigateToMySavingsMobilize(product) }
        })
    }

    return moreOptions
}

/*************************************************
*                  MoreOptions                   *
*************************************************/
const moreOptionsClickedHandler = ({ optionClicked, product }) => {
    if (product.termAccountProduct.canBeIncreased) {
        product.moreOptions[optionClicked].callbackFunction(product)
    }
}

const navigateToMySavingsCharge = product => {
    navigateByName(routeList.SAVINGS_MY_SAVINGS_PORTFOLIO_CHARGE_NAME, { params: product })
}

const navigateToMySavingsMobilize = product => {
    navigateByName(routeList.SAVINGS_MY_SAVINGS_PORTFOLIO_MOBILIZE_NAME, { params: product })
}

/*************************************************
*                                                *
*          Portfolio Products List Request       *
*                                                *
*************************************************/
const initialRequest = async () => {
    showLoader.value = true
    applyErrorState.value = false

	try {
        await portfolioFetch()
		showLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, initialRequest)
	}
}

/*************************************************
*                                                *
*                 Error Feedback                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
    showFeedbackToast.value = true
}

initialRequest()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
</style>
